import { useEffect, useMemo, useState } from 'react';
import AppLoader from 'components/general/AppLoader';
import { Button, Form, Image } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'components/general/ViewportSelect';
import { toast } from 'react-toastify';
import { canManagePageBuilder, isMemberEquivalent } from 'utils/acl';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';

const getAvatarSrc = (avatar) => resolveAvatarSrc(avatar);

export default function AttendanceCard({ meeting, getMeeting }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const url = process.env.REACT_APP_URL;

  const canManageAttendance = canManagePageBuilder(authUser);

  const [theme, setTheme] = useState(document.body.getAttribute('data-theme') || 'light');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [attendanceAvatars, setAttendanceAvatars] = useState({});

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.getAttribute('data-theme') || 'light');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setAttendance(Array.isArray(meeting?.meetingDetails?.attendance) ? meeting.meetingDetails.attendance : []);
  }, [meeting?.meetingDetails?.attendance]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!authUser?.token) {
        // Not signed in: skip protected users endpoint silently
        setUsers([]);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${url}/users`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            // Unauthorized: likely token expired or not allowed — don't spam with errors
            setUsers([]);
            return;
          }
          throw new Error(response.statusText);
        }

        const data = await response.json();
        setUsers(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(`Failed to load users: ${error.message}`, { position: 'bottom-left' });
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [authUser?.token, url]);

  useEffect(() => {
    let mounted = true
    try {
      (attendance || []).forEach((user) => {
        const key = String(user?.tmAuthUserId || user?.id || user?._id || '').trim()
        if (!key) return
        const existing = getAvatarSrc(user?.avatarUrl || user?.avatar)
        if (existing) return
        if (attendanceAvatars[key]) return

        (async () => {
          try {
            const resp = await fetch(`${url}/unified/${encodeURIComponent(key)}`)
            if (!mounted) return
            if (!resp.ok) return
            const payload = await resp.json().catch(() => null)
            const avatar = payload?.identity?.avatarUrl || payload?.identity?.avatar || null
            const src = avatar ? resolveAvatarSrc(avatar) : null
            if (src && mounted) setAttendanceAvatars(prev => ({ ...prev, [key]: src }))
          } catch (_err) {
            // ignore
          }
        })()
      })
    } catch (_err) {
      // ignore
    }

    return () => { mounted = false }
  }, [attendance, attendanceAvatars, url])

  const userOptions = useMemo(() => {
    const sortUsers = (list) => [...list].sort((a, b) => `${a?.firstName || ''} ${a?.lastName || ''}`.localeCompare(`${b?.firstName || ''} ${b?.lastName || ''}`));
    const allUsers = sortUsers(users);

    const officerMembers = allUsers.filter(user => isMemberEquivalent(user.acl));
    const guests = allUsers.filter(user => user.acl === 'Guest');

    return [
      {
        label: 'Members',
        options: officerMembers.map(user => ({ value: user._id, label: `${user.firstName} ${user.lastName}` }))
      },
      {
        label: 'Guests',
        options: guests.map(user => ({ value: user._id, label: `${user.firstName} ${user.lastName}` }))
      }
    ];
  }, [users]);

  const attendanceIds = attendance.map(user => String(user?.id || user?._id || '')).filter(Boolean);
  const selectedUser = userOptions.flatMap(group => group.options).find(option => option.value === selectedUserId) || null;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#23272b' : '#fff',
      color: theme === 'dark' ? '#fff' : '#222',
      borderColor: state.isFocused ? (theme === 'dark' ? '#FFD700' : '#004165') : (theme === 'dark' ? '#444' : '#ccc'),
      boxShadow: state.isFocused ? `0 0 0 2px ${theme === 'dark' ? '#FFD700' : '#004165'}` : 'none',
      minHeight: '44px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#23272b' : '#fff',
      color: theme === 'dark' ? '#fff' : '#222',
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 2000,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#fff' : '#222',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? (theme === 'dark' ? '#FFD700' : '#004165')
        : state.isFocused
          ? (theme === 'dark' ? 'rgba(255,215,0,0.08)' : 'rgba(0,65,101,0.08)')
          : (theme === 'dark' ? '#23272b' : '#fff'),
      color: state.isSelected
        ? (theme === 'dark' ? '#23272b' : '#fff')
        : (theme === 'dark' ? '#fff' : '#222'),
      fontWeight: state.isSelected ? 700 : 400,
    }),
    input: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#fff' : '#222',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#888' : '#888',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#FFD700' : '#004165',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#FFD700' : '#004165',
    }),
  };

  const saveAttendance = async (userId, attending) => {
    const response = await fetch(`${url}/meeting/${meeting?.meetingDetails?.id}/attendance/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authUser.token}`,
      },
      body: JSON.stringify({ attending }),
    });

    if (!response.ok) {
      let message = response.statusText;
      try {
        const errorData = await response.json();
        message = errorData?.Error || errorData?.error || message;
      } catch {
        // Keep the HTTP status text fallback.
      }
      throw new Error(message);
    }

    await getMeeting();
  };

  const handleAddUser = async () => {
    if (!selectedUserId || attendanceIds.includes(selectedUserId)) {
      setSelectedUserId('');
      return;
    }

    const selectedUser = users.find(user => String(user?._id || '') === String(selectedUserId));
    setAttendance(prev => [...prev, selectedUser].filter(Boolean));
    setSelectedUserId('');
    try {
      await saveAttendance(selectedUserId, true);
    } catch (error) {
      setAttendance(prev => prev.filter(user => String(user?.id || user?._id || '') !== String(selectedUserId)));
      toast.error(`Failed to save attendance: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const handleRemoveUser = async (userId) => {
    const removedUser = attendance.find(user => String(user?.id || user?._id || '') === userId) || null;
    setAttendance(prev => prev.filter(user => String(user?.id || user?._id || '') !== userId));
    try {
      await saveAttendance(userId, null);
    } catch (error) {
      if (removedUser) {
        setAttendance(prev => [...prev, removedUser]);
      }
      toast.error(`Failed to save attendance: ${error.message}`, { position: 'bottom-left' });
    }
  };

  return (
    <div style={{ color: 'var(--foreground)' }}>
      {!canManageAttendance ? (
        <div className="text-center py-4" style={{ opacity: 0.85 }}>Only officers can manage attendance.</div>
      ) : isLoading ? (
        <AppLoader />
      ) : (
        <Form data-bs-theme={theme === 'dark' ? 'dark' : 'light'}>
          <Form.Group className="mb-3">
            <Select
              options={userOptions}
              className="formInput"
              value={selectedUser}
              onChange={option => setSelectedUserId(option ? option.value : '')}
              isClearable
              isSearchable
              styles={customStyles}
              placeholder="Select a user"
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              maxMenuHeight={220}
            />
          </Form.Group>

          <Button type="button" variant="success" className="mb-4 w-100" onClick={handleAddUser} disabled={!selectedUserId}>
            Add to Attendance
          </Button>

          <div className="d-grid gap-2">
            {attendance.length === 0 ? (
              <div style={{ opacity: 0.85 }}>No attendees added yet.</div>
            ) : (
              attendance.map(user => {
                  const userId = String(user?.id || user?._id || '');
                  const key = String(user?.tmAuthUserId || user?.id || user?._id || '').trim();
                  const avatarSrc = getAvatarSrc(user?.avatarUrl || user?.avatar);
                  const effectiveSrc = avatarSrc || (key ? attendanceAvatars[key] : null);
                  const userInitials = getUserInitials(user, 'T');
                return (
                  <div key={userId} className="d-flex align-items-center justify-content-between p-2 rounded" style={{ background: 'rgba(128,128,128,0.08)' }}>
                    <div className="d-flex align-items-center gap-2">
                        {effectiveSrc ? (
                          <Image src={effectiveSrc} roundedCircle style={{ width: 32, height: 32, objectFit: 'cover' }} />
                        ) : (
                        <span
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.12)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: 12
                          }}
                        >
                          {userInitials}
                        </span>
                      )}
                      <span>{`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'TBA'}</span>
                    </div>
                    <Button type="button" variant="outline-danger" size="sm" onClick={() => handleRemoveUser(userId)}>
                      <i className="bi bi-trash" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </Form>
      )}
    </div>
  );
}